import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import ar from './locales/ar.json';

export type Locale = 'en' | 'ar';

const DICTS: Record<Locale, Record<string, string>> = {
  en: en as Record<string, string>,
  ar: ar as Record<string, string>,
};

const LOCALE_STORAGE_KEY = 'slighter.locale';

const RTL_LOCALES: Locale[] = ['ar'];

interface I18nContextValue {
  locale: Locale;
  setLocale: (loc: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitial(): Locale {
  // Product decision (2026-06-12): English only. The ar dictionary and RTL
  // plumbing stay dormant for a possible future launch, but the app must
  // never auto-select Arabic from the browser language.
  return 'en';
}

/** Replaces {placeholders} with vars[name]. Falls back to {name} if missing. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(detectInitial);

  // Apply <html lang> + dir=rtl/ltr so Tailwind RTL utilities flow correctly.
  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    html.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
  }, [locale]);

  const setLocale = useCallback((loc: Locale) => {
    setLocaleState(loc);
    try { localStorage.setItem(LOCALE_STORAGE_KEY, loc); } catch { /* ignore */ }
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const dict = DICTS[locale];
    const raw = dict[key] ?? DICTS.en[key] ?? key;
    return interpolate(raw, vars);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => ({
    locale, setLocale, t, isRTL: RTL_LOCALES.includes(locale),
  }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Defensive fallback — useful during incremental rollout where some
    // routes might mount outside the provider.
    return {
      locale: 'en',
      setLocale: () => undefined,
      t: (k, v) => interpolate(DICTS.en[k] ?? k, v),
      isRTL: false,
    };
  }
  return ctx;
}
